import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { GRID_CELL_DATA_QA } from 'search/enums/constants';
import {
  getAppliedFilters,
  getCurrentSort,
  getImageType,
} from 'shared/utils/gridEventTrackingUtils';
import { getSearchFiltersForPdp } from 'productTemplate/utils/getSearchFiltersForPdp';
import { GridCellLink } from 'shared/components/Grid/GridCellLink';
import { GridCellProductInfo } from 'shared/components/Grid/GridCellProductInfo';
import { GridCellWrapper } from 'shared/components/Grid/styles/GridStyles';
import { isTouchDevice } from 'shared/utils/isTouchDevice';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setSearchFiltersForPdp } from 'store/slice/searchSlice';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { VALUE_TO_NAME_PRODUCT_CONDITION_MAP } from 'server/grpc/search/search.productSearch.requester';
import { SHOE_CONDITIONS } from 'shared/enums/ConditionsEnums';
import { GridCellProductImage } from './ConsumerSearchGridCellProductImage';

const GridCellSizePrice = dynamic(() => import('shared/components/Grid/GridCellSizePrice'), {
  ssr: false,
});

interface IGridCell {
  disableLazyLoad?: boolean;
  position: number;
  product: {
    category: string;
    id: string;
    imageUrl?: string;
    productCondition?: string;
    productType: string;
    releaseYear?: number;
    seasonYear?: string;
    slug: string;
    title: string;
    releaseDateDisplay?: string;
  };
  resultsCount?: number;
  price?: number;
  retailPrice?: number;
}

export const ConsumerSearchGridCell = ({
  disableLazyLoad = false,
  product,
  position,
  price,
  retailPrice,
}: IGridCell) => {
  const [isActiveCell, setIsActiveCell] = useState(false);
  const debouncedIsActiveCell = useDebouncedValue(isActiveCell, 100);
  const { query } = useRouter();
  const { query: searchQuery, sortBy, sortOrder, recently_released: recentlyReleased } = query;
  const { user } = useStoreSelector((state) => state.auth);
  const storeDispatch = useStoreDispatch();

  const { category, id, imageUrl, productCondition, productType, releaseDateDisplay, slug, title } =
    product;

  const isUsed = VALUE_TO_NAME_PRODUCT_CONDITION_MAP[productCondition] === SHOE_CONDITIONS.USED;

  const imageType = getImageType(imageUrl, isUsed);
  const eventTrackingEventContext = useEventContext();

  const handleMouseEnter = () => {
    if (isTouchDevice()) return;
    setIsActiveCell(true);
  };

  const handleMouseLeave = () => {
    if (isTouchDevice()) return;
    setIsActiveCell(false);
  };

  const handleOnClick = () => {
    // Store the search filters to apply to PDP's Buy Button
    storeDispatch(setSearchFiltersForPdp(getSearchFiltersForPdp(query)));

    sendTrackingEvent('CONSTRUCTOR_GRID_CELL_TAP', {
      currentSort: getCurrentSort(sortBy, sortOrder, recentlyReleased),
      event_context: eventTrackingEventContext,
      is_filtered: getAppliedFilters(query) !== '',
      is_preview: false,
      objectID: id,
      pdp_image_type: imageType,
      position: position?.toString(),
      service_name: 'consumer_search',
      product_template_slug: slug,
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(searchQuery && { search_string: searchQuery.toString() }),
      ...(user && { userID: user.id }),
    });
  };

  return (
    <GridCellWrapper
      data-qa={GRID_CELL_DATA_QA}
      data-grid-cell-position={position}
      data-grid-cell-name={id}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        height: '100%',
      }}
    >
      <GridCellLink
        aria-label={normalizeValue(slug)}
        onClick={handleOnClick}
        to={`/${category === 'shoes' ? 'sneakers' : 'apparel'}/${slug}`}
      >
        <GridCellProductInfo
          id={id}
          isActiveCell={isActiveCell}
          name={title}
          price={price}
          productCondition={VALUE_TO_NAME_PRODUCT_CONDITION_MAP[productCondition]}
          releaseDate={releaseDateDisplay}
          retailPrice={retailPrice}
          slug={slug}
        />
        {debouncedIsActiveCell && (
          <GridCellSizePrice productTemplateId={id} retailPrice={retailPrice} />
        )}
        <GridCellProductImage
          disableLazyLoad={disableLazyLoad}
          imageSrc={imageUrl}
          isActiveCell={isActiveCell}
          isSellerImage={false}
          name={title}
          productType={productType}
        />
      </GridCellLink>
    </GridCellWrapper>
  );
};
